import React, { type JSX } from 'react'
import { isErrored, isLoading } from '@rwwa/rwwa-data-access/dist/data-access'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { LoadingPlaceholder } from '@mobi/component-library/Misc/LoadingPlaceholder'
import { ErrorMessage } from '@core/Components/Messages'
import { NoticeBox } from '@core/Components/NoticeBox'
import { FavouriteNumberPoolResults } from '@core/Data/favouriteNumbersResults'
import { useDataAccess } from '@core/Utils/hooks/useDataAccess'
import { HeaderStyled } from './FavouriteNumbers.styles'
import { Leg } from './NumberResultsPools'
import {
  DateComponentStyled,
  DetailTextStyled,
  FavNumberResultListStyled,
  JackpotStyled,
  NavComponentStyled,
  NextButtonStyled,
  NumberResultsTabStyled,
  OrderStyled,
  PoolInfoComponentStyled,
  PoolInfoLineLeftStyled,
  PoolInfoLineRightStyled,
  PoolInfoLineStyled,
  PrevButtonStyled,
  RaceStyled,
  ResultButtonStyled,
  ResultLegendComponentStyled,
  ResultLiStyled,
  ResultTitleStyled,
  ResultsStyled,
  SubStyled,
} from './NumberResultsTab.styles'
import { setResultKey } from './Store'
import { selectResultKey } from './Store/selectors'

export const NumberResultsTab = () => {
  const dispatch = useDispatch()
  const resultKey = useSelector(selectResultKey)
  const state = useDataAccess(FavouriteNumberPoolResults, resultKey)

  if (isErrored(state.data)) {
    return <ErrorMessage>Unable to retrieve results</ErrorMessage>
  }

  if (isLoading(state.data)) {
    return (
      <div style={{ margin: '1em' }}>
        <LoadingPlaceholder width='100%' height='4em' />
      </div>
    )
  }

  const currentPool = state.data
  const isNextAvailable = currentPool.isNextAvailable
  const isPreviousAvailable = currentPool.isPreviousAvailable

  const handletPreviousButtonClick = () => {
    if (currentPool.isPreviousAvailable) {
      dispatch(setResultKey(`prev|${currentPool.poolDate}`))
    }
  }

  const handleNextButtonClick = () => {
    if (currentPool.isNextAvailable) {
      dispatch(setResultKey(`next|${currentPool.poolDate}`))
    }
  }

  if (!currentPool) {
    return null
  }
  const isNumberAllocated = !!currentPool.legs

  return (
    <NumberResultsTabStyled data-tid-number-results-content=''>
      <NavComponent
        isPreviousAvailable={isPreviousAvailable}
        isNextAvailable={isNextAvailable}
        poolDate={currentPool.poolDate}
        handletPreviousButtonClick={handletPreviousButtonClick}
        handleNextButtonClick={handleNextButtonClick}
      />
      <HeaderStyled data-tid-pool-info-header='' style={{ margin: '1rem 0' }}>
        POOL INFO
      </HeaderStyled>
      <PoolInfoComponent
        poolTotal={currentPool.poolTotal}
        poolResultDisplayDateTitle={currentPool.poolResultDisplayDateTitle}
        poolResultDisplayDate={currentPool.poolResultDisplayDate}
        jackpotAmount={currentPool.jackpotAmount}
      />
      <HeaderStyled data-tid-numbers-results-header='' style={{ margin: '1rem 0' }}>
        NUMBERS AND RESULTS
      </HeaderStyled>
      {isNumberAllocated && (
        <>
          <ResultDetails
            requiredNumber={currentPool.numbersRequired}
            dividendPayable={currentPool.dividendPayable}
            carryover={currentPool.carryover}
            winningEntries={currentPool.winningEntries}
            data-tid-result-details=''
          />
          <ResultsStyled>
            <FavnumberResultList data-tid-favnumber-result-list legs={currentPool.legs} />
            <ResultLegendComponent data-tid-favnumber-result-legend />
          </ResultsStyled>
        </>
      )}
      {!isNumberAllocated && (
        <div style={{ margin: '1rem', display: 'flex', justifyContent: 'center' }}>
          <NoticeBox
            title='Favourite Numbers have not yet been allocated.'
            noticeBoxType='Warning'
            hasBorder
          />
        </div>
      )}
    </NumberResultsTabStyled>
  )
}

interface ResultDetailsProps {
  requiredNumber: string
  dividendPayable: string
  carryover: string
  winningEntries: number
}

const ResultDetails = ({
  requiredNumber,
  dividendPayable,
  carryover,
  winningEntries,
}: ResultDetailsProps) => {
  let winningEntriesText = 'There are no winning entries.'

  if (winningEntries == 1) {
    winningEntriesText = 'There is 1 winning entry.'
  } else if (winningEntries > 1) {
    winningEntriesText = `There are ${winningEntries.toLocaleString()} winning entries.`
  }

  const showDividendPayable = dividendPayable && dividendPayable.length > 0 && requiredNumber

  return (
    <>
      {showDividendPayable && (
        <DetailTextStyled id='dividendPayable' data-tid-number-results-dividend-payable=''>
          {requiredNumber} numbers required for a dividend of {dividendPayable}.
        </DetailTextStyled>
      )}
      {carryover && (
        <DetailTextStyled id='carryover' data-tid-number-results-carryover=''>
          There is a carryover of {carryover}.
        </DetailTextStyled>
      )}
      {showDividendPayable && (
        <DetailTextStyled id='winningEntries' data-tid-number-winning-entries=''>
          {winningEntriesText}
        </DetailTextStyled>
      )}{' '}
    </>
  )
}

const FavnumberResultList = ({ legs }: { legs: Leg[] }) => {
  const resultBoxList = legs.map((data, index) => {
    return (
      <ResultButton
        key={index}
        order={index + 1}
        race={data.race}
        isLive={data.isLive}
        isWinner={data.isWinner}
        isSub={data.isSub}
      />
    )
  })
  return <FavNumberResultListStyled>{resultBoxList}</FavNumberResultListStyled>
}

interface ResultButtonProps {
  order: number
  race: string
  isLive: boolean
  isWinner: boolean
  isSub: boolean
}

const ResultLegendComponent = () => {
  return (
    <ResultLegendComponentStyled data-tid-number-result-legend=''>
      <ResultTitleStyled>Legend</ResultTitleStyled>
      <ul style={{ listStyle: 'none', margin: '0', padding: '0', display: 'flex' }}>
        <ResultLiStyled>
          <ResultButton order={21} race='AD2' isLive={false} isWinner={true} isSub={false} />
          Won
        </ResultLiStyled>
        <ResultLiStyled>
          <ResultButton order={17} race='BD1' isLive={false} isWinner={false} isSub={false} />
          Lost
        </ResultLiStyled>
        <ResultLiStyled>
          <ResultButton order={11} race='PR6' isLive={true} isWinner={false} isSub={false} />
          Live
        </ResultLiStyled>
      </ul>
    </ResultLegendComponentStyled>
  )
}

const ResultButton = ({ order, race, isLive, isWinner, isSub }: ResultButtonProps): JSX.Element => {
  return (
    <ResultButtonStyled
      isLive={isLive}
      isWinner={isWinner}
      data-tid-number-results-result-button=''
    >
      <OrderStyled>{order}</OrderStyled>
      <RaceStyled>{race}</RaceStyled>
      {isSub && <SubStyled>(SUB)</SubStyled>}
    </ResultButtonStyled>
  )
}

const NavComponent = ({
  isPreviousAvailable,
  isNextAvailable,
  poolDate,
  handletPreviousButtonClick,
  handleNextButtonClick,
}: {
  isPreviousAvailable: boolean
  isNextAvailable: boolean
  poolDate: string
  handletPreviousButtonClick: () => void
  handleNextButtonClick: () => void
}) => {
  return (
    <NavComponentStyled>
      <PrevButtonStyled
        onClick={handletPreviousButtonClick}
        isPreviousAvailable={isPreviousAvailable}
        data-tid-number-results-prev-button=''
      >
        {'< '}PREV
      </PrevButtonStyled>
      <DateComponentStyled data-tid-number-results-date-button=''>{poolDate}</DateComponentStyled>
      <NextButtonStyled
        onClick={handleNextButtonClick}
        isNextAvailable={isNextAvailable}
        data-tid-number-results-next-button=''
      >
        NEXT{' >'}
      </NextButtonStyled>
    </NavComponentStyled>
  )
}

interface PoolInfoComponentProps {
  poolTotal: string
  poolResultDisplayDateTitle: string
  poolResultDisplayDate: string
  jackpotAmount: string
}

const PoolInfoComponent = ({
  poolTotal,
  poolResultDisplayDateTitle,
  poolResultDisplayDate,
  jackpotAmount,
}: PoolInfoComponentProps) => {
  return (
    <PoolInfoComponentStyled>
      <PoolInfoLineStyled>
        <PoolInfoLineLeftStyled data-tid-number-pool-total-title=''>
          Pool Total
        </PoolInfoLineLeftStyled>
        <PoolInfoLineRightStyled data-tid-number-pool-total=''>
          {poolTotal}
          {jackpotAmount && (
            <JackpotStyled>
              <Icon size='2.1rem' type='jackpot' color='#f2a900' />
              {jackpotAmount}
            </JackpotStyled>
          )}
        </PoolInfoLineRightStyled>
      </PoolInfoLineStyled>
      <PoolInfoLineStyled>
        <PoolInfoLineLeftStyled data-tid-number-results-display-date-title=''>
          {poolResultDisplayDateTitle}
        </PoolInfoLineLeftStyled>
        <PoolInfoLineRightStyled data-tid-number-results-display-date=''>
          {poolResultDisplayDate}
        </PoolInfoLineRightStyled>
      </PoolInfoLineStyled>
    </PoolInfoComponentStyled>
  )
}
